import { isAcTerminalId } from "./ac";
import { clearAiEstimates } from "./ai/component-draft";
import { FUSE_TYPES_BY_AIC } from "./fuse-aic";
import type { Cable, ComponentDefinition, ComponentNode, FuseType } from "./types";

/** Match the distribution-category cleanup in setCustomCategory, without
 * importing the store into this pure planner. No new ratings are seeded. */
const NON_DISTRIBUTION_SPECS = [
  "capacityAh", "voltage", "watts", "ampDraw", "ampOutput", "ampSurge",
  "peakSunHoursDefault", "chargeAmpsTypical", "chemistry", "maxDischargeA",
  "maxChargeA", "recommendedChargeA", "chargePlanningA", "chargerMethodChargeA",
  "chargingNotes", "coldCrankingAmps", "marineCrankingAmps", "shortCircuitA",
  "pvMaxVoc", "pvVoc", "dutyHoursPerDay", "alwaysOn", "loadCurrentModel",
] as const satisfies readonly (keyof ComponentDefinition)[];

/** An explicit setup choice can replace a load placeholder, but must not
 * reinterpret an active device or an already declared internal circuit. */
const SPECIAL_BEHAVIOR_FIELDS = [
  "acVoltage", "internalDcConnections", "oneWayDcConnections", "currentShunt",
  "fuseBusGroups", "dcBreaker", "simulateStates", "settings", "bms",
  "inputVoltage", "outputVoltage", "conversionEfficiency", "maxInputA",
  "hybridSourceOutputLimitA", "outputCurrentLimitsA", "ampOutput",
  "chargeAmpsTypical", "capacityAh", "chemistry", "pvMaxVoc", "pvVoc",
  "pvMaxSeriesFuseA",
] as const satisfies readonly (keyof ComponentDefinition)[];

function hasPlainDcFuseBlockShape(def: ComponentDefinition): boolean {
  if (def.id !== "custom" || def.requestedModel ||
    (def.category !== "load" && def.category !== "distribution") ||
    (def.renderVariant != null && def.renderVariant !== "card") ||
    SPECIAL_BEHAVIOR_FIELDS.some(field => def[field] != null)) return false;

  const terminals = def.terminals;
  return terminals.length >= 3 &&
    new Set(terminals.map(t => t.id)).size === terminals.length &&
    terminals.every(t => t.id.length > 0 && t.polarity === "positive" && !isAcTerminalId(t.id) &&
      (t.direction === "input" || t.direction === "output")) &&
    terminals.filter(t => t.direction === "input").length === 1 &&
    terminals.filter(t => t.direction === "output").length >= 2;
}

export function canConfigureDcFuseBlock(def: ComponentDefinition): boolean {
  return hasPlainDcFuseBlockShape(def) && !def.isFuseBlock && def.fuseSlotType == null;
}

/** Used to keep identity editing available without reopening topology setup. */
export function isConfiguredDcFuseBlock(def: ComponentDefinition): boolean {
  return hasPlainDcFuseBlockShape(def) && def.category === "distribution" &&
    def.isFuseBlock === true && def.fuseSlotType != null && FUSE_TYPES_BY_AIC.includes(def.fuseSlotType);
}

/** Explicitly declare the existing single input as feeding every output slot.
 * Terminal ampacity and old automatic suggestions never become fuse ratings. */
export function configureDcFuseBlock(
  node: ComponentNode,
  cables: readonly Cable[],
  family: FuseType,
): { node: ComponentNode; cables: Cable[] } | null {
  const def = node.customDefinition;
  if (node.definitionId !== "custom" || !def || !canConfigureDcFuseBlock(def) ||
    !FUSE_TYPES_BY_AIC.includes(family)) return null;

  const nextDef: ComponentDefinition = {
    ...def,
    category: "distribution",
    isFuseBlock: true,
    fuseSlotType: family,
    equipmentLimitExemption: "fuse",
  };
  for (const field of NON_DISTRIBUTION_SPECS) delete nextDef[field];
  const { dutyHoursPerDayOverride: _duty, alwaysOnOverride: _always, ...rest } = node;
  const outputs = new Set(def.terminals.filter(t => t.direction === "output").map(t => t.id));
  return {
    node: {
      ...rest,
      customDefinition: clearAiEstimates(nextDef, [
        "category", "isFuseBlock", "fuseSlotType", "equipmentLimitExemption", ...NON_DISTRIBUTION_SPECS,
      ]),
    },
    cables: cables.map(cable => {
      const onOutput = (cable.fromNodeId === node.id && outputs.has(cable.fromTerminalId)) ||
        (cable.toNodeId === node.id && outputs.has(cable.toTerminalId));
      if (!onOutput || cable.fuseAmpAuto !== true) return cable;
      const { fuseAmpOverride: _suggestion, ...withoutRating } = cable;
      // False also prevents a later auto-fuse refresh from filling this slot.
      // A separate explicit family override remains a user declaration.
      return { ...withoutRating, fuseAmpAuto: false };
    }),
  };
}
